import React, { useState, useEffect, useRef } from 'react';
import { Form, Input, Button, Upload, message, Space } from 'antd';
import { InboxOutlined, EditOutlined, EyeOutlined, PictureOutlined } from '@ant-design/icons';
import styles from './PostForm.module.scss';
import { api } from '../../config/api';
import { markdownToHtml, normalizeToMarkdown } from '../../utils/markdown';

interface PostFormProps {
  onSubmit: (postData: FormData) => void;
  initialValues?: any;
  isEditing?: boolean;
  category: number;
}

const PostForm: React.FC<PostFormProps> = ({ onSubmit, initialValues, isEditing = false, category }) => {
  const [form] = Form.useForm();
  const [fileList, setFileList] = useState<any[]>([]);
  const [contentMarkdown, setContentMarkdown] = useState<string>('');
  const [contentTouched, setContentTouched] = useState<boolean>(false);
  const [isPreviewMode, setIsPreviewMode] = useState<boolean>(false);
  const [isUploadingImage, setIsUploadingImage] = useState<boolean>(false);
  const textareaRef = useRef<any>(null);

  useEffect(() => {
    if (initialValues) {
      const normalizedContent = normalizeToMarkdown(initialValues.content);
      form.setFieldsValue({
        title: initialValues.title,
        content: normalizedContent,
      });
      setContentMarkdown(normalizedContent);
      setFileList(initialValues.files?.map((file: any, index: number) => ({
        uid: `-${index}`,
        name: file.fileName,
        status: 'done',
        url: file.fileUrl,
      })) || []);
      setContentTouched(false);
    } else {
      form.resetFields();
      form.setFieldsValue({ content: '' });
      setFileList([]);
      setContentMarkdown('');
      setContentTouched(false);
    }
  }, [initialValues, form]);

  const handleContentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setContentTouched(true);
    setContentMarkdown(value);
    form.setFieldsValue({ content: value });
  };

  const handleImageUpload = async () => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');

    input.onchange = async (e: any) => {
      const file = e.target?.files?.[0];
      if (file) {
        setIsUploadingImage(true);
        try {
          const response = await api.uploadFileAsync(file, 'community');
          const imageUrl = response.data.url;

          // Insert markdown image syntax at cursor position
          const textarea = textareaRef.current?.resizableTextArea?.textArea;
          if (textarea) {
            const start = textarea.selectionStart;
            const end = textarea.selectionEnd;
            const text = contentMarkdown;
            const before = text.substring(0, start);
            const after = text.substring(end);
            const imageMarkdown = `![이미지 설명](${imageUrl})`;
            const newText = before + imageMarkdown + after;

            setContentMarkdown(newText);
            form.setFieldsValue({ content: newText });
            setContentTouched(true);

            // Set cursor position after inserted image
            setTimeout(() => {
              textarea.selectionStart = textarea.selectionEnd = start + imageMarkdown.length;
              textarea.focus();
            }, 0);
          } else {
            // Fallback: append to end
            const imageMarkdown = `\n![이미지 설명](${imageUrl})\n`;
            const newText = contentMarkdown + imageMarkdown;
            setContentMarkdown(newText);
            form.setFieldsValue({ content: newText });
            setContentTouched(true);
          }

          message.success('이미지가 업로드되었습니다.');
        } catch (error) {
          console.error('이미지 업로드 실패:', error);
          message.error('이미지 업로드에 실패했습니다.');
        } finally {
          setIsUploadingImage(false);
        }
      }
    };

    input.click();
  };

  const buildFormData = (values: any): FormData => {
    const postData = new FormData();
    postData.append('title', values.title);
    postData.append('content', contentMarkdown);
    postData.append('category', category.toString());

    const attachments: string[] = [];

    fileList.forEach((file) => {
      if (file.originFileObj) {
        postData.append('files', file.originFileObj);
      } else if (file.url) {
        attachments.push(file.url);
      }
    });

    postData.append('attachments', JSON.stringify(attachments));
    return postData;
  };

  const handleSubmit = (values: any) => {
    if (!contentMarkdown || contentMarkdown.trim() === '') {
      setContentTouched(true);
      message.error('내용을 입력해주세요.');
      return;
    }

    const postData = buildFormData(values);
    onSubmit(postData);

    if (!isEditing) {
      form.resetFields();
      setFileList([]);
      setContentMarkdown('');
      setContentTouched(false);
      setIsPreviewMode(false);
    }
  };

  const handleUpload = ({ file, onSuccess }: any) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const url = reader.result as string;
      onSuccess(url, file);
    };
  };

  const beforeUpload = (file: File) => {
    const isLt10M = file.size / 1024 / 1024 < 10;
    if (!isLt10M) {
      message.error('파일 크기는 10MB 이하여야 합니다!');
    }
    return isLt10M;
  };

  const renderPreview = () => {
    if (!contentMarkdown || contentMarkdown.trim() === '') {
      return <div className={styles.emptyPreview}>미리보기할 내용이 없습니다.</div>;
    }
    const html = markdownToHtml(contentMarkdown);
    return <div className={styles.previewContent} dangerouslySetInnerHTML={{ __html: html }} />;
  };

  return (
    <div className={styles.postFormContainer}>
      <Form form={form} onFinish={handleSubmit} layout="vertical" className={styles.postForm}>
        <Form.Item name="title" label="제목" rules={[{ required: true, message: '제목을 입력해주세요' }]}>
          <Input size="large" placeholder="게시글 제목을 입력하세요" />
        </Form.Item>
        <Form.Item
          label={
            <div className={styles.contentLabel}>
              <span>내용 <span className={styles.required}>*</span></span>
              <Space size="small">
                <Button
                  size="small"
                  type={!isPreviewMode ? 'primary' : 'default'}
                  icon={<EditOutlined />}
                  onClick={() => setIsPreviewMode(false)}
                >
                  작성
                </Button>
                <Button
                  size="small"
                  type={isPreviewMode ? 'primary' : 'default'}
                  icon={<EyeOutlined />}
                  onClick={() => setIsPreviewMode(true)}
                >
                  미리보기
                </Button>
                {!isPreviewMode && (
                  <Button
                    size="small"
                    icon={<PictureOutlined />}
                    onClick={handleImageUpload}
                    loading={isUploadingImage}
                    disabled={isUploadingImage}
                  >
                    이미지 삽입
                  </Button>
                )}
              </Space>
            </div>
          }
          validateStatus={contentTouched && (!contentMarkdown || contentMarkdown.trim() === '') ? 'error' : ''}
          help={contentTouched && (!contentMarkdown || contentMarkdown.trim() === '') ? '내용을 입력해주세요' : undefined}
        >
          {!isPreviewMode ? (
            <>
              <Input.TextArea
                ref={textareaRef}
                value={contentMarkdown}
                onChange={handleContentChange}
                placeholder="마크다운 형식으로 게시글 내용을 입력하세요

예시:
# 제목
## 부제목
**굵은 글씨**
*이탤릭*
- 목록 항목
```
코드 블록
```
[링크](https://example.com)
![이미지](이미지URL)"
                className={styles.contentTextarea}
                rows={15}
              />
              <div className={styles.markdownHelp}>
                <small>
                  <strong>마크다운 문법:</strong>{' '}
                  <code># 제목</code> | <code>## 소제목</code> | <code>**굵게**</code> | <code>*기울임*</code> |{' '}
                  <code>- 항목</code> | <code>`코드`</code> | <code>[링크](URL)</code> | <code>![이미지](URL)</code>
                </small>
              </div>
            </>
          ) : (
            <div className={styles.previewContainer}>
              {renderPreview()}
            </div>
          )}
        </Form.Item>
        <Form.Item name="content" hidden>
          <Input type="hidden" />
        </Form.Item>
        <Form.Item name="attachments" label="파일 첨부">
          <Upload.Dragger
            customRequest={handleUpload}
            onChange={({ fileList }) => setFileList(fileList)}
            multiple
            listType="picture"
            fileList={fileList}
            beforeUpload={beforeUpload}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">클릭하거나 파일을 이 영역으로 드래그하세요</p>
            <p className="ant-upload-hint">
              모든 종류의 파일을 업로드할 수 있습니다. 파일 크기는 10MB 이하여야 합니다.
            </p>
          </Upload.Dragger>
        </Form.Item>
        <Form.Item>
          <Button type="primary" htmlType="submit" size="large">
            {isEditing ? '수정' : '등록'}
          </Button>
        </Form.Item>
      </Form>
    </div>
  );
};

export default PostForm;
